import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { POST_REGISTER_LOGIN, SIGNUP_URL } from '../../config/endpoints';
import { handleRegisterError } from '../../config/errors';
import { useNavigate } from 'react-router-dom';
import { setIsLoginDialog } from "../../redux/reducers/loginSlice";
import { useDispatch } from 'react-redux';
import validator from "email-validator";
import { toast, ToastContainer } from "react-toastify";
import emailjs from "emailjs-com";
import "react-toastify/dist/ReactToastify.css";
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import 'flag-icons/css/flag-icons.min.css';
import ReactGA from 'react-ga4';
import { motion, AnimatePresence } from 'framer-motion';

const SignupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const otpInputs = useRef([]);

  const [data, setData] = useState({
    role: 'player',
    name: '',
    dob: '',
    gender: '',
    email: '',
    phone: '',
    coach: '',
    height: '',
    weight: '',
    countryCode: '+91',
    sport: '',
    organization: '',
    location: '',
    contactName: '',
    websiteUrl: '',
  });

  const [lockedRole, setLockedRole] = useState(null);
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [showRoleMismatchMessage, setShowRoleMismatchMessage] = useState(false);
  const [error, setError] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showDuplicateSignupDialog, setShowDuplicateSignupDialog] = useState(false);
  const [showSignupLimitDialog, setShowSignupLimitDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Sign Up successful. Welcome!');
  const [autoCloseProgress, setAutoCloseProgress] = useState(0);

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [filteredCountries, setFilteredCountries] = useState([]);

  const [showOTPBox, setShowOTPBox] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(30);

  const { role, name, dob, gender, height, weight, email, coach, phone, countryCode, sport, organization, location, contactName, websiteUrl } = data;
  const enteredOTP = otp.join('');

  // PreSign integration
  useEffect(() => {
    const checkPresignRole = () => {
      try {
        const preRole = localStorage.getItem('PRESIGN_ROLE');
        if (preRole && ['player', 'coach', 'organization'].includes(preRole)) {
          const roleSwitchDetected = localStorage.getItem('ROLE_SWITCH_DETECTED');
          const previousRoleForSwitch = localStorage.getItem('PREVIOUS_ROLE_FOR_SWITCH');

          if (roleSwitchDetected === '1' && previousRoleForSwitch) {
            setShowRoleMismatchMessage(true);
            setData((prev) => ({ ...prev, previousRoleForMessage: previousRoleForSwitch }));
            try { ReactGA.event({ category: 'Signup', action: 'role_switch_detected', label: `${previousRoleForSwitch}->${preRole}` }); } catch { }
            localStorage.removeItem('ROLE_SWITCH_DETECTED');
            localStorage.removeItem('PREVIOUS_ROLE_FOR_SWITCH');
          }

          setData((prev) => ({ ...prev, role: preRole }));
          setLockedRole(preRole);
          try { ReactGA.event({ category: 'Signup', action: 'role_locked_detected', label: preRole }); } catch { }
        }
      } catch { }
    };

    checkPresignRole();
    const handleStorageChange = (e) => {
      if (e.key === 'PRESIGN_ROLE') checkPresignRole();
    };
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(checkPresignRole, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Prefill phone from PreSign
  useEffect(() => {
    try {
      const cc = localStorage.getItem('PRESIGN_CC');
      const pn = localStorage.getItem('PRESIGN_PHONE');
      if (cc || pn) {
        setData((prev) => ({
          ...prev,
          countryCode: cc || prev.countryCode,
          phone: pn || prev.phone,
        }));
        if (pn) setPhoneLocked(true);
      }
    } catch { }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setIsLoginDialog(false));
  }, [dispatch]);

  useEffect(() => {
    try { ReactGA.event({ category: 'Signup', action: 'view' }); } catch { }
  }, []);

  // Browser back button handling
  useEffect(() => {
    try {
      if (sessionStorage.getItem('fromPreSignNav') === '1') {
        sessionStorage.setItem('openPreSignOnReturn', '1');
      }
    } catch { }

    const firedRef = { current: false };
    const redirectOnce = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      try { sessionStorage.setItem('openPreSignOnReturn', '1'); } catch { }
      navigate('/', { replace: true });
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
    };

    const onPop = () => redirectOnce();
    const onHash = () => {
      if (window.location.hash !== '#/signup') redirectOnce();
    };

    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
    };
  }, [navigate]);

  // Resend timer
  useEffect(() => {
    let intervalId;
    if (resendDisabled) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 0) {
            clearInterval(intervalId);
            setResendDisabled(false);
            return 30;
          } else {
            return prevTimer - 1;
          }
        });
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [resendDisabled]);

  // Country data
  const getCountriesData = () => {
    const countryList = getCountries();
    const preferredCountries = ['US', 'GB', 'IN', 'CA', 'AU', 'DE', 'FR', 'JP', 'SG', 'NL'];

    const sortedCountries = countryList.sort((a, b) => {
      const aPreferred = preferredCountries.includes(a);
      const bPreferred = preferredCountries.includes(b);
      if (aPreferred && !bPreferred) return -1;
      if (!aPreferred && bPreferred) return 1;
      return a.localeCompare(b);
    });

    return sortedCountries.map(country => ({
      code: country,
      name: new Intl.DisplayNames(['en'], { type: 'region' }).of(country) || country,
      dialCode: `+${getCountryCallingCode(country)}`,
      isPreferred: preferredCountries.includes(country)
    }));
  };

  const getCountryFlagClass = (countryCode) => `fi fi-${countryCode.toLowerCase()}`;

  const getCountryCodeFromDialCode = (dialCode) => {
    const country = filteredCountries.find(c => c.dialCode === dialCode);
    if (country) return country.code;
    const allCountries = getCountriesData();
    const foundCountry = allCountries.find(c => c.dialCode === dialCode);
    return foundCountry ? foundCountry.code : 'US';
  };

  useEffect(() => {
    if (countrySearch.trim() === "") {
      setFilteredCountries(getCountriesData());
    } else {
      const allCountries = getCountriesData();
      const filtered = allCountries.filter(country =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.dialCode.includes(countrySearch)
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearch]);

  useEffect(() => {
    const initialCountries = getCountriesData();
    setFilteredCountries(initialCountries);
    if (!countryCode || countryCode === '+1') {
      const defaultCountry = initialCountries.find(c => c.code === 'IN');
      if (defaultCountry) {
        setData(prev => ({ ...prev, countryCode: defaultCountry.dialCode }));
      }
    }
  }, []);

  const selectCountry = (code) => {
    const country = filteredCountries.find(c => c.code === code);
    if (country) {
      setData(prev => ({ ...prev, countryCode: country.dialCode }));
      if (error.phone) setError(prev => ({ ...prev, phone: '' }));
      if (phone) {
        const rules = getPhoneValidationRules(country.dialCode);
        if (phone.length < rules.minLength || phone.length > rules.maxLength || !rules.pattern.test(phone)) {
          setError(prev => ({ ...prev, phone: rules.message }));
        }
      }
    }
    setShowCountryDropdown(false);
    setCountrySearch("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const countrySelector = event.target.closest('.country-selector-wrapper');
      if (showCountryDropdown && !countrySelector) {
        setShowCountryDropdown(false);
        setCountrySearch("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCountryDropdown]);

  const handleChange = (e) => {
    setErrorMsg("");
    const { name, value } = e.target;
    if (phoneLocked && name === 'phone') return;

    setData((prevData) => ({ ...prevData, [name]: value }));
    setError((prevError) => ({ ...prevError, [name]: "" }));

    if (name === 'height' || name === 'weight') {
      const regex = /^$|^\d{1,3}(\.\d{0,2})?$/;
      if (regex.test(value) || value === '') {
        setData(prev => ({ ...prev, [name]: value }));
      }
    }

    if (name === 'phone') {
      if (!/^\d*$/.test(value)) {
        setError(prev => ({ ...prev, phone: 'Only numeric values are allowed' }));
      } else {
        const rules = getPhoneValidationRules(countryCode);
        if (value.length === 0) {
          setError(prev => ({ ...prev, phone: '' }));
        } else if (value.length < rules.minLength || value.length > rules.maxLength || !rules.pattern.test(value)) {
          setError(prev => ({ ...prev, phone: rules.message }));
        } else {
          setError(prev => ({ ...prev, phone: '' }));
        }
      }
    }
  };

  const handleChangeOTP = (index, value, e) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      otpInputs.current[index + 1]?.focus();
    }

    if (!value && e.inputType === 'deleteContentBackward' && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && index > 0 && !otp[index]) {
      otpInputs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < otp.length - 1) {
      otpInputs.current[index + 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setTimer(30);
    setResendDisabled(true);
    setErrorMsg("A new OTP has been sent. Please check your messages.");
    try { ReactGA.event({ category: 'Signup', action: 'resend_otp' }); } catch { }
  };

  const validateEmail = (email) => {
    if (!validator.validate(email)) return "Please enter a valid email address";
    const domainPart = email.split("@")[1];
    if (domainPart) {
      const domainSections = domainPart.split(".");
      const tld = domainSections[domainSections.length - 1];
      if (tld.length < 2) return "Please enter a valid email address with a proper domain";
      if (domainSections[0].length < 2) return "Please enter a valid email address with a proper domain name";
    }
    return "";
  };

  const normalizeWebsite = (url) => {
    try {
      const s = String(url || '').trim();
      if (!s) return '';
      if (/^https?:\/\//i.test(s)) return s;
      return `https://${s}`;
    } catch {
      return url;
    }
  };

  const checkIfItIsOnlyNumber = (possibleNumber) => /^[0-9]+$/.test(possibleNumber);
  const checkIfItValidHeight = (height) => height >= 75 && height <= 225;
  const checkIfItValidWeight = (weight) => weight >= 25 && weight <= 250;

  const getPhoneValidationRules = (countryCode) => {
    const rules = {
      '+91': { minLength: 10, maxLength: 10, pattern: /^\d{10}$/, message: 'Indian phone number must be exactly 10 digits' },
      '+1': { minLength: 10, maxLength: 10, pattern: /^\d{10}$/, message: 'US/Canada phone number must be exactly 10 digits' },
      '+44': { minLength: 10, maxLength: 11, pattern: /^\d{10,11}$/, message: 'UK phone number must be 10-11 digits' },
      '+61': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'Australian phone number must be exactly 9 digits' },
      '+49': { minLength: 10, maxLength: 12, pattern: /^\d{10,12}$/, message: 'German phone number must be 10-12 digits' },
      '+33': { minLength: 9, maxLength: 10, pattern: /^\d{9,10}$/, message: 'French phone number must be 9-10 digits' },
      '+81': { minLength: 10, maxLength: 11, pattern: /^\d{10,11}$/, message: 'Japanese phone number must be 10-11 digits' },
      '+65': { minLength: 8, maxLength: 8, pattern: /^\d{8}$/, message: 'Singapore phone number must be exactly 8 digits' },
      '+31': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'Dutch phone number must be exactly 9 digits' },
      '+971': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'UAE phone number must be exactly 9 digits' }
    };
    return rules[countryCode] || {
      minLength: 7,
      maxLength: 15,
      pattern: /^\d{7,15}$/,
      message: `Phone number must be 7-15 digits for ${countryCode}`
    };
  };

  const currentValidationRules = getPhoneValidationRules(countryCode);

  const buildSignupPlainText = (form) => {
    const lines = [];
    const roleVal = form?.role || '-';
    const nameVal = roleVal === 'organization' ? (form?.contactName || form?.organization || 'Organization') : (form?.name || 'User');
    lines.push(`Role: ${roleVal}`);
    lines.push(`Name: ${nameVal}`);
    if (form?.organization) lines.push(`Organization: ${form.organization}`);
    if (form?.sport) lines.push(`Sport: ${form.sport}`);
    if (form?.email) lines.push(`Email: ${form.email}`);
    if (form?.countryCode || form?.phone) lines.push(`Phone: ${(form?.countryCode || '')} ${(form?.phone || '')}`.trim());
    if (form?.gender) lines.push(`Gender: ${form.gender}`);
    if (form?.dob) lines.push(`DOB: ${form.dob}`);
    if (form?.height) lines.push(`Height (cm): ${form.height}`);
    if (form?.weight) lines.push(`Weight (kg): ${form.weight}`);
    if (form?.coach) lines.push(`Coach: ${form.coach}`);
    if (form?.location) lines.push(`Location: ${form.location}`);
    if (form?.websiteUrl) lines.push(`Website: ${form.websiteUrl}`);
    return lines.join('\n');
  };

  const sendSignupDetailsEmail = async (form) => {
    const displayName = form.role === 'organization' ? (form.contactName || form.organization || 'Org') : (form.name || 'User');
    const subjectOrg = form.organization ? ` • ${form.organization}` : '';
    const subject = `New Signup • ${displayName}${subjectOrg} • ${form.sport || 'N/A'}`;
    const notifyEmail = process.env.REACT_APP_SIGNUP_NOTIFY_EMAIL || 'support@visist.ai';
    const EMAILJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_fsityif';
    const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_3elyneh';
    const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'xIhcseyB7eZFS6bHt';

    const templateParams = {
      subject,
      to_name: 'VISIST AI',
      to_email: notifyEmail,
      from_name: form.role === 'organization' ? (form.contactName || 'Organization') : (form.name || 'User'),
      name: form.role === 'organization' ? (form.contactName || 'Organization') : (form.name || 'User'),
      email: form.email,
      message: buildSignupPlainText(form),
    };

    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, templateParams, EMAILJS_PUBLIC_KEY);
      try { toast.success('Signup notification email queued.'); } catch { }
    } catch (err) {
      try { console.error('Signup email send failed:', err?.message || err); } catch { }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try { ReactGA.event({ category: 'Signup', action: 'submit', label: (role || '').toLowerCase() }); } catch { }

    let formErrors = {};
    const normalizedRole = (role || '').toLowerCase();

    if (normalizedRole !== 'organization') {
      if (!name) formErrors.name = "Please enter your full name";
    } else {
      if (!organization) formErrors.organization = "Please enter your organization name";
      if (!contactName) formErrors.contactName = "Please enter a contact name";
    }

    if (!sport) formErrors.sport = "Please select your preferred sport";

    if (height) {
      if (!checkIfItIsOnlyNumber(height)) {
        formErrors.height = "Please enter number only";
      } else if (!checkIfItValidHeight(height)) {
        formErrors.height = `Height has to be range of 75 cms - 225 cms`;
      }
    }

    if (weight) {
      if (!checkIfItIsOnlyNumber(weight)) {
        formErrors.weight = "Please enter number only";
      } else if (!checkIfItValidWeight(weight)) {
        formErrors.weight = `Weight has to be range of 25 kgs - 250 kgs`;
      }
    }

    if (!phone) {
      formErrors.phone = "Phone number is required.";
    } else if (phone.length < 7 || phone.length > 15) {
      formErrors.phone = "Phone number must be between 7-15 digits";
    }

    if (!email) {
      formErrors.email = "Please enter your email";
    } else {
      const emailError = validateEmail(email);
      if (emailError) formErrors.email = emailError;
    }

    if (error && error.phone) formErrors.phone = error.phone;

    if (Object.keys(formErrors).length > 0) {
      setError(formErrors);
      return;
    }

    const profileBase = (
      normalizedRole === 'organization'
        ? { preferredSport: sport, organizationEmail: email }
        : { preferredSport: sport, email }
    );

    const websiteNormalized = (websiteUrl || '').trim() ? normalizeWebsite(websiteUrl.trim()) : '';
    const profileOptional = {
      ...(normalizedRole !== 'organization' && name ? { fullName: name } : {}),
      ...(dob ? { dob } : {}),
      ...(gender ? { gender } : {}),
      ...(weight !== '' && weight != null ? { weight: Number(weight) } : {}),
      ...(height !== '' && height != null ? { height: Number(height) } : {}),
      ...(location ? { location } : {}),
      ...(coach ? { coachName: coach } : {}),
      ...(organization ? { organizationName: organization } : {}),
      ...(normalizedRole === 'organization' && contactName ? { contactName } : {}),
      ...(websiteNormalized ? { website: websiteNormalized } : {}),
    };

    const payload = {
      cc: countryCode,
      number: phone,
      role: normalizedRole,
      profile: { ...profileBase, ...profileOptional },
    };

    try {
      const res = await axios.post(SIGNUP_URL, payload, { headers: { 'Content-Type': 'application/json' } });
      if (res.status >= 200 && res.status < 300 && (res.data?.success !== false)) {
        try { ReactGA.event({ category: 'Signup', action: 'submit_success', label: normalizedRole }); } catch { }
        setSuccessMessage('Thanks for signing up. Your sign in information will be shared with you on your registered email.');
        setShowSuccessDialog(true);
        sendSignupDetailsEmail({ role, name, dob, gender, email, phone, countryCode, coach, height, weight, sport, organization, location, contactName, websiteUrl });
      } else {
        const msg = res?.data?.message || 'Failed to submit signup. Please try again.';
        try { ReactGA.event({ category: 'Signup', action: 'submit_error', label: msg }); } catch { }
        setErrorMsg(msg);
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        try { ReactGA.event({ category: 'Signup', action: 'duplicate_signup_detected' }); } catch { }
        setShowDuplicateSignupDialog(true);
        return;
      }
      const apiMsg = err?.response?.data?.message || err?.message || 'Failed to submit signup. Please try again.';
      try { ReactGA.event({ category: 'Signup', action: 'submit_error', label: apiMsg }); } catch { }
      setErrorMsg(apiMsg);
    }
  };

  const handleVerifyOTP = async () => {
    try { console.log('[Signup] Verify OTP clicked'); } catch { }
    setLoading(true);

    const profileVerifyBase = (
      (role || '').toLowerCase() === 'organization'
        ? { preferredSport: sport, organizationEmail: email }
        : { preferredSport: sport, email }
    );
    const profileVerifyOptional = {
      ...((role || '').toLowerCase() !== 'organization' && name ? { fullName: name } : {}),
      ...(dob ? { dob } : {}),
      ...(gender ? { gender } : {}),
      ...(weight !== '' && weight != null ? { weight: Number(weight) } : {}),
      ...(height !== '' && height != null ? { height: Number(height) } : {}),
      ...(location ? { location } : {}),
      ...(coach ? { coachName: coach } : {}),
      ...(organization ? { organizationName: organization } : {}),
      ...(((role || '').toLowerCase() === 'organization') && contactName ? { contactName } : {}),
      ...((websiteUrl || '').trim() ? { website: normalizeWebsite(websiteUrl.trim()) } : {}),
    };
    const newPayload = {
      cc: countryCode,
      number: phone,
      role: (role || '').toLowerCase(),
      profile: { ...profileVerifyBase, ...profileVerifyOptional },
      otp: enteredOTP.replace(/\s+/g, ""),
    };

    const BQS_ID = localStorage.getItem("SIGN_UP_BQS__ID");
    const url = POST_REGISTER_LOGIN + "/" + BQS_ID;

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newPayload),
    })
      .then((res) => res.json())
      .then((response) => {
        const regErrorMessage = handleRegisterError(response);
        if (response && "code" in response && response["code"] == 200) {
          try { ReactGA.event({ category: 'Signup', action: 'verify_success' }); } catch { }
          localStorage.setItem("BQS__ID", response.data["_id"]);
          localStorage.setItem("BQS_TOKEN", response.data.token.token);
          localStorage.setItem("BQS_ROLE", response.data.role);
          setSuccessMessage("Welcome to VISIST! Your account has been created successfully.");
          setShowSuccessDialog(true);
          setShowOTPBox(false);
          setTimeout(() => {
            if (showSuccessDialog) {
              setShowSuccessDialog(false);
              try {
                if ((role || '').toLowerCase() !== 'coach') {
                  sessionStorage.setItem('openPreSignOnReturn', '1');
                  sessionStorage.setItem('openPreSignStep', '3');
                  sessionStorage.setItem('openPreSignShowExploreInfo', '1');
                }
              } catch { }
              const membershipProfile = response?.data?.profileData?.membershipProfile;
              // Strict check: Only treat users as having an active subscription if they have an explicit active plan.
              const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
              const isEnforced = enforcement === 'hard';
              const hasActiveSubscription = !isEnforced || (membershipProfile && membershipProfile.isActive === true);
              
              // Mandatory redirect to plan selection page for players/orgs if they don't have a plan
              const redirectPath = (role || '').toLowerCase() === 'coach' 
                  ? '/' 
                  : (hasActiveSubscription ? '/game-posts/game-videos' : '/subscription-pricing');
              navigate(redirectPath, { replace: true });
            }
          }, 2500);
        } else {
          setErrorMsg(handleRegisterError(response));
        }
      })
      .catch((error) => {
        console.error("Exception Login API: ", error);
        setErrorMsg(error.message || "Something went wrong! Try again later.");
      })
      .finally(() => setLoading(false));
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    try {
      if ((role || '').toLowerCase() !== 'coach') {
        sessionStorage.setItem('openPreSignOnReturn', '1');
        sessionStorage.setItem('openPreSignStep', '3');
        sessionStorage.setItem('openPreSignShowExploreInfo', '1');
      }
    } catch { }
    try { ReactGA.event({ category: 'Signup', action: 'success_dialog_close' }); } catch { }
    
    // Default redirect to plan selection page for players/orgs during first-time signup
    const redirectPath = (role || '').toLowerCase() === 'coach' ? '/' : '/subscription-pricing';
    navigate(redirectPath, { replace: true });
  };

  const handleCloseDuplicateSignupDialog = () => {
    setShowDuplicateSignupDialog(false);
    try { ReactGA.event({ category: 'Signup', action: 'duplicate_dialog_close' }); } catch { }
    navigate('/', { replace: true });
  };

  const handleContinueClick = () => {
    try {
      const raw = localStorage.getItem('LOGIN_COUNT');
      const count = Number(raw || '0') || 0;
      if (count > 5) {
        setShowSignupLimitDialog(true);
        setShowSuccessDialog(false);
        try { ReactGA.event({ category: 'Signup', action: 'continue_click_limit_dialog' }); } catch { }
        return;
      }
    } catch { }
    try { ReactGA.event({ category: 'Signup', action: 'continue_click' }); } catch { }
    handleCloseSuccessDialog();
  };

  useEffect(() => {
    if (!showSuccessDialog) {
      setAutoCloseProgress(0);
      return;
    }
    let rafId;
    const duration = 2500;
    const start = performance.now();
    const step = (ts) => {
      const elapsed = ts - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setAutoCloseProgress(pct);
      if (pct < 100) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [showSuccessDialog]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {/* Main Container - Dark Premium Design with Sports Animations */}
      <div className="min-h-screen relative overflow-hidden pt-28 pb-12 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #0a1f14 0%, #0d2818 50%, #051a0d 100%)' }}>

        {/* World-Class Animation Keyframes */}
        <style>{`
          @keyframes particleFloat {
            0%, 100% { transform: translate(0, 0); opacity: 0.3; }
            25% { transform: translate(30px, -50px); opacity: 0.8; }
            50% { transform: translate(-20px, -100px); opacity: 0.6; }
            75% { transform: translate(40px, -75px); opacity: 0.7; }
          }
          
          @keyframes auroraPulse {
            0%, 100% { opacity: 0.3; filter: blur(80px) hue-rotate(0deg); transform: scale(1) translateY(0); }
            33% { opacity: 0.5; filter: blur(100px) hue-rotate(30deg); transform: scale(1.2) translateY(-20px); }
            66% { opacity: 0.4; filter: blur(90px) hue-rotate(-30deg); transform: scale(1.1) translateY(20px); }
          }
          
          @keyframes meshShift {
            0%, 100% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(50px, 30px) scale(1.05); }
          }
          
          @keyframes nodeGlow {
            0%, 100% { opacity: 0.4; transform: scale(1); box-shadow: 0 0 20px rgba(204,255,0,0.3); }
            50% { opacity: 0.8; transform: scale(1.3); box-shadow: 0 0 40px rgba(204,255,0,0.6); }
          }
          
          @keyframes lineConnect {
            0% { stroke-dashoffset: 500; opacity: 0.2; }
            50% { opacity: 0.6; }
            100% { stroke-dashoffset: 0; opacity: 0.2; }
          }
          
          @keyframes radialBurst {
            0% { transform: scale(0.8) rotate(0deg); opacity: 0.2; }
            50% { transform: scale(1.2) rotate(180deg); opacity: 0.4; }
            100% { transform: scale(0.8) rotate(360deg); opacity: 0.2; }
          }
          
          @keyframes depthLayer {
            0%, 100% { transform: translateZ(0) translateY(0); opacity: 0.15; }
            50% { transform: translateZ(50px) translateY(-30px); opacity: 0.3; }
          }
        `}</style>

        {/* Epic Background Layers */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ perspective: '1000px' }}>

          {/* Aurora Gradient Layers */}
          <div className="absolute -top-1/2 -right-1/4 w-[900px] h-[900px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(16,185,129,0.4) 0%, rgba(5,150,105,0.2) 50%, transparent 70%)',
              animation: 'auroraPulse 15s ease-in-out infinite',
              filter: 'blur(80px)'
            }} />
          <div className="absolute top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(52,211,153,0.35) 0%, rgba(16,185,129,0.15) 50%, transparent 70%)',
              animation: 'auroraPulse 18s ease-in-out infinite 5s',
              filter: 'blur(90px)'
            }} />
          <div className="absolute bottom-1/4 right-1/3 w-[700px] h-[700px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(110,231,183,0.3) 0%, rgba(52,211,153,0.12) 50%, transparent 70%)',
              animation: 'auroraPulse 20s ease-in-out infinite 10s',
              filter: 'blur(100px)'
            }} />

          {/* Animated Mesh Grid */}
          <div className="absolute inset-0 opacity-10" style={{ animation: 'meshShift 40s ease-in-out infinite' }}>
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="meshGrid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                  <circle cx="40" cy="40" r="1.5" fill="#ccff00" opacity="0.6" />
                  <line x1="40" y1="0" x2="40" y2="80" stroke="#ccff00" strokeWidth="0.5" opacity="0.3" />
                  <line x1="0" y1="40" x2="80" y2="40" stroke="#ccff00" strokeWidth="0.5" opacity="0.3" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#meshGrid)" />
            </svg>
          </div>

          {/* Particle Network System */}
          {[...Array(12)].map((_, i) => (
            <div
              key={`particle-${i}`}
              className="absolute w-3 h-3 rounded-full"
              style={{
                left: `${15 + (i * 7)}%`,
                top: `${20 + (i * 5) % 60}%`,
                background: 'radial-gradient(circle, #ccff00 0%, rgba(204,255,0,0.3) 70%, transparent 100%)',
                animation: `particleFloat ${8 + i * 2}s ease-in-out infinite ${i * 0.5}s`,
                boxShadow: '0 0 20px rgba(204,255,0,0.5)'
              }}
            />
          ))}

          {/* Connecting Lines Between Particles */}
          <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ccff00" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {[...Array(8)].map((_, i) => (
              <line
                key={`line-${i}`}
                x1={`${15 + i * 12}%`}
                y1={`${25 + i * 8}%`}
                x2={`${25 + i * 11}%`}
                y2={`${35 + i * 7}%`}
                stroke="url(#lineGrad)"
                strokeWidth="1"
                strokeDasharray="5,3"
                style={{ animation: `lineConnect ${10 + i * 2}s linear infinite ${i * 1.5}s` }}
              />
            ))}
          </svg>

          {/* Glowing Nodes */}
          {[...Array(6)].map((_, i) => (
            <div
              key={`node-${i}`}
              className="absolute w-4 h-4 rounded-full bg-emerald-400"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i * 10) % 50}%`,
                animation: `nodeGlow ${6 + i}s ease-in-out infinite ${i * 2}s`
              }}
            />
          ))}

          {/* Radial Burst Patterns */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-15" style={{ animation: 'radialBurst 30s linear infinite' }}>
            <svg width="100%" height="100%" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
              {[...Array(12)].map((_, i) => (
                <line
                  key={`burst-${i}`}
                  x1="200"
                  y1="200"
                  x2={200 + Math.cos((i * 30 * Math.PI) / 180) * 180}
                  y2={200 + Math.sin((i * 30 * Math.PI) / 180) * 180}
                  stroke="#ccff00"
                  strokeWidth="2"
                  opacity={0.3 + (i % 3) * 0.1}
                />
              ))}
              <circle cx="200" cy="200" r="150" stroke="#ccff00" strokeWidth="1" fill="none" opacity="0.2" />
              <circle cx="200" cy="200" r="100" stroke="#ccff00" strokeWidth="1" fill="none" opacity="0.3" />
              <circle cx="200" cy="200" r="50" stroke="#ccff00" strokeWidth="1.5" fill="none" opacity="0.4" />
            </svg>
          </div>

          {/* 3D Depth Layers */}
          <div className="absolute bottom-1/3 left-1/5 w-80 h-80 opacity-12" style={{ animation: 'depthLayer 25s ease-in-out infinite', transformStyle: 'preserve-3d' }}>
            <svg width="100%" height="100%" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="depthGrad">
                  <stop offset="0%" stopColor="#ccff00" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.1" />
                </radialGradient>
              </defs>
              <polygon points="150,30 270,90 270,210 150,270 30,210 30,90" fill="url(#depthGrad)" opacity="0.3" />
              <polygon points="150,60 240,105 240,195 150,240 60,195 60,105" fill="none" stroke="#ccff00" strokeWidth="1.5" opacity="0.5" />
              <polygon points="150,90 210,120 210,180 150,210 90,180 90,120" fill="none" stroke="#ccff00" strokeWidth="1" opacity="0.4" />
            </svg>
          </div>

          {/* Flowing Energy Waves */}
          <svg className="absolute inset-0 w-full h-full opacity-12" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ccff00" stopOpacity="0" />
                <stop offset="50%" stopColor="#ccff00" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[...Array(5)].map((_, i) => (
              <path
                key={`wave-${i}`}
                d={`M0,${200 + i * 80} Q250,${150 + i * 80} 500,${200 + i * 80} T1000,${200 + i * 80}`}
                stroke="url(#waveGrad)"
                strokeWidth="3"
                fill="none"
                opacity={0.4 - i * 0.05}
                style={{ animation: `lineConnect ${15 + i * 3}s ease-in-out infinite ${i * 2}s` }}
              />
            ))}
          </svg>

          {/* Geometric Constellation */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-8" style={{ animation: 'meshShift 50s ease-in-out infinite reverse' }}>
            <svg width="100%" height="100%" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
              <circle cx="300" cy="100" r="4" fill="#ccff00" opacity="0.8" />
              <circle cx="450" cy="200" r="3" fill="#ccff00" opacity="0.7" />
              <circle cx="500" cy="400" r="4" fill="#ccff00" opacity="0.8" />
              <circle cx="300" cy="500" r="3" fill="#ccff00" opacity="0.7" />
              <circle cx="100" cy="400" r="4" fill="#ccff00" opacity="0.8" />
              <circle cx="150" cy="200" r="3" fill="#ccff00" opacity="0.7" />
              <line x1="300" y1="100" x2="450" y2="200" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
              <line x1="450" y1="200" x2="500" y2="400" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
              <line x1="500" y1="400" x2="300" y2="500" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
              <line x1="300" y1="500" x2="100" y2="400" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
              <line x1="100" y1="400" x2="150" y2="200" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
              <line x1="150" y1="200" x2="300" y2="100" stroke="#ccff00" strokeWidth="1" opacity="0.3" />
            </svg>
          </div>

        </div>

        {/* Premium Noise Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.3'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />

        <div className="max-w-xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-black text-white mb-3" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>Create Your Account</h2>
            <p className="text-white flex items-center justify-center gap-2 text-lg">
              <span className="text-2xl">👋</span>
              <span>Welcome to VISIST {role === 'player' ? 'Player' : role === 'coach' ? 'Coach' : 'Organization'} Account</span>
            </p>
            <p className="mt-3 text-sm text-white/90">We never share your details.</p>
          </div>

          {/* Glassmorphism Form Card */}
          <div className="backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
            <form onSubmit={submit}>
              {/* Role Selection */}
              {!lockedRole && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-white mb-3">Select Role</label>
                  <div className="flex gap-3">
                    {['player', 'coach', 'organization'].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setData(prev => ({ ...prev, role: r }))}
                        className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${role === r
                          ? 'bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white shadow-lg shadow-emerald-500/30'
                          : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                          }`}
                      >
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Role Mismatch Message */}
              {showRoleMismatchMessage && lockedRole && (
                <div className="mb-6 bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-3 backdrop-blur-sm">
                  <span className="text-xl">⚠️</span>
                  <span className="text-sm text-yellow-200 flex-1">
                    You have selected <strong className="text-yellow-100">{(data.previousRoleForMessage || lockedRole).charAt(0).toUpperCase() + (data.previousRoleForMessage || lockedRole).slice(1)}</strong> role in your First Attempt!
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowRoleMismatchMessage(false)}
                    className="text-yellow-200 hover:text-yellow-100 text-xl"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Required Fields */}
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-bold text-white">Required Information</h3>

                {role !== 'organization' && (
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={handleChange}
                      placeholder="Full Name *"
                      className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                    />
                    {error.name && <p className="mt-1 text-sm text-red-400">{error.name}</p>}
                  </div>
                )}

                {role === 'organization' && (
                  <>
                    <div>
                      <input
                        type="text"
                        name="organization"
                        value={organization}
                        onChange={handleChange}
                        placeholder="Organization Name *"
                        className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                      />
                      {error.organization && <p className="mt-1 text-sm text-red-400">{error.organization}</p>}
                    </div>
                    <div>
                      <input
                        type="text"
                        name="contactName"
                        value={contactName}
                        onChange={handleChange}
                        placeholder="Contact Name *"
                        className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                      />
                      {error.contactName && <p className="mt-1 text-sm text-red-400">{error.contactName}</p>}
                    </div>
                  </>
                )}

                <div>
                  <select
                    name="sport"
                    value={sport}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                  >
                    <option value="" disabled>Preferred Sport *</option>
                    <option value="Badminton">Badminton</option>
                    <option value="Pickleball" disabled>Pickle ball</option>
                    <option value="Cricket" disabled>Cricket</option>
                    <option value="Basketball" disabled>Basketball</option>
                    <option value="Multi Sports">Multi Sports</option>
                  </select>
                  {error.sport && <p className="mt-1 text-sm text-red-400">{error.sport}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    placeholder="Email ID *"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                  />
                  {error.email && <p className="mt-1 text-sm text-red-400">{error.email}</p>}
                </div>

                {/* Phone Number with Country Code */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Phone Number *</label>
                  <div className="flex gap-2">
                    <div className="relative country-selector-wrapper">
                      <button
                        type="button"
                        onClick={() => !phoneLocked && setShowCountryDropdown(!showCountryDropdown)}
                        disabled={phoneLocked}
                        className={`flex items-center gap-2 px-3 py-3.5 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all text-white ${phoneLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className={`${getCountryFlagClass(getCountryCodeFromDialCode(countryCode))}`}></span>
                        <span className="font-medium">{countryCode}</span>
                        <span className="text-xs">▼</span>
                      </button>

                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-96 overflow-hidden">
                          <div className="p-3 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search countries..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                            />
                          </div>
                          <div className="max-h-80 overflow-y-auto">
                            {filteredCountries.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">No countries found</div>
                            ) : (
                              filteredCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => selectCountry(country.code)}
                                  className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors ${countryCode === country.dialCode ? 'bg-emerald-50' : ''
                                    }`}
                                >
                                  <span className={`${getCountryFlagClass(country.code)}`}></span>
                                  <span className="flex-1 text-left text-sm">{country.name}</span>
                                  <span className="text-sm text-gray-600">{country.dialCode}</span>
                                  {countryCode === country.dialCode && <span className="text-emerald-600">✓</span>}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <input
                      type="tel"
                      name="phone"
                      value={phone}
                      onChange={handleChange}
                      placeholder="Phone Number *"
                      maxLength={currentValidationRules.maxLength}
                      readOnly={phoneLocked}
                      className={`flex-1 px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all placeholder-white/50 backdrop-blur-sm ${phoneLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ color: '#ccff00' }}
                    />
                  </div>
                  {error.phone && <p className="mt-1 text-sm text-red-400">{error.phone}</p>}
                </div>
              </div>

              {/* Optional Fields */}
              {(role === 'player' || role === 'coach' || role === 'organization') && (
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-bold text-white">Optional Information</h3>

                  {role === 'player' && (
                    <>
                      <div>
                        <input
                          type="date"
                          name="dob"
                          value={dob}
                          onChange={handleChange}
                          placeholder="Date of Birth"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Gender</label>
                        <div className="flex gap-3">
                          {['male', 'female', 'other'].map((g) => (
                            <button
                              key={g}
                              type="button"
                              onClick={() => setData(prev => ({ ...prev, gender: g }))}
                              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-all ${gender === g
                                ? 'bg-emerald-400 border border-emerald-400'
                                : 'bg-white/10 text-white/70 border border-white/10 hover:bg-white/20'
                                }`}
                              style={gender === g ? { color: '#ccff00' } : {}}
                            >
                              {g === 'other' ? 'Prefer Not to Say' : g.charAt(0).toUpperCase() + g.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <input
                          type="text"
                          name="location"
                          value={location}
                          onChange={handleChange}
                          placeholder="Location"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <input
                            type="text"
                            name="weight"
                            value={weight}
                            onChange={handleChange}
                            placeholder="Weight (kg)"
                            className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                          />
                          {error.weight && <p className="mt-1 text-sm text-red-400">{error.weight}</p>}
                        </div>
                        <div>
                          <input
                            type="text"
                            name="height"
                            value={height}
                            onChange={handleChange}
                            placeholder="Height (cm)"
                            className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                          />
                          {error.height && <p className="mt-1 text-sm text-red-400">{error.height}</p>}
                        </div>
                      </div>

                      <div>
                        <input
                          type="text"
                          name="coach"
                          value={coach}
                          onChange={handleChange}
                          placeholder="Coach Name"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>
                    </>
                  )}

                  {role === 'coach' && (
                    <>
                      <div>
                        <input
                          type="text"
                          name="organization"
                          value={organization}
                          onChange={handleChange}
                          placeholder="Organization Name"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          name="location"
                          value={location}
                          onChange={handleChange}
                          placeholder="Location"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>
                    </>
                  )}

                  {role === 'organization' && (
                    <>
                      <div>
                        <input
                          type="text"
                          name="location"
                          value={location}
                          onChange={handleChange}
                          placeholder="Location"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>
                      <div>
                        <input
                          type="url"
                          name="websiteUrl"
                          value={websiteUrl}
                          onChange={handleChange}
                          placeholder="Website URL"
                          className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/50 backdrop-blur-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Error Message */}
              {errorMsg && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-sm backdrop-blur-sm">
                  {errorMsg}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white font-bold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Sign Up'}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-white">
              Already have an account?{' '}
              <button
                onClick={() => {
                  dispatch(setIsLoginDialog(true));
                  navigate('/');
                }}
                className="font-semibold hover:opacity-80"
                style={{ color: '#ccff00' }}
              >
                Sign In
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <AnimatePresence>
        {showOTPBox && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full"
            >
              <h2 className="text-2xl font-black text-gray-900 mb-2 text-center">Verify OTP</h2>
              <p className="text-gray-600 mb-6 text-center">Enter the 4-digit code sent to your phone</p>

              <div className="flex gap-3 justify-center mb-6">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChangeOTP(index, e.target.value, e)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                  />
                ))}
              </div>

              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
                  {errorMsg}
                </div>
              )}

              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.some(d => !d)}
                className="w-full py-3 px-6 bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="text-center">
                <button
                  onClick={handleResendOTP}
                  disabled={resendDisabled}
                  className="text-sm text-emerald-600 font-semibold hover:text-emerald-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {resendDisabled ? `Resend OTP in ${timer}s` : 'Resend OTP'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence >

      {/* Success Dialog */}
      < AnimatePresence >
        {showSuccessDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
            >
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
                <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">
                {name ? `Welcome, ${name.split(' ')[0]}!` : 'Welcome!'}
              </h2>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <div className="w-full h-2 bg-gray-200 rounded-full mb-6 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-100"
                  style={{ width: `${autoCloseProgress}%` }}
                />
              </div>
              <div className="flex justify-center">
                <button
                  onClick={handleContinueClick}
                  className="w-full py-3 px-6 bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  Back to Home
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence >

      {/* Duplicate Signup Dialog */}
      < AnimatePresence >
        {showDuplicateSignupDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
            >
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
                <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">We already have your details</h2>
              <p className="text-gray-600 mb-6">We will get back to you soon through your registered mail</p>
              <div className="flex justify-center">
                <button
                  onClick={handleCloseDuplicateSignupDialog}
                  className="w-full py-3 px-6 bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  Back to Home
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence >

      {/* Signup Limit Dialog */}
      < AnimatePresence >
        {showSignupLimitDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
            >
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200 flex items-center justify-center">
                <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">Signup Limit Reached</h2>
              <p className="text-gray-600 mb-6">You have reached the maximum number of signup attempts. Please contact support.</p>
              <button
                onClick={() => {
                  setShowSignupLimitDialog(false);
                  navigate('/', { replace: true });
                }}
                className="w-full py-3 px-6 bg-gradient-to-br from-[#026B33] to-[#00A13A] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                Back to Home
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence >
    </>
  );
};

export default SignupPage;
